import { useState } from 'react'
import { WarningCircleIcon, CheckCircleIcon, InfoIcon } from '@phosphor-icons/react'
import {
    ScoopText,
    ScoopChip,
    ScoopTree,
    ScoopSkeleton,
    ScoopLinearProgress,
    ScoopColumn,
    ScoopRow,
    ScoopTab,
} from '../../ui'
import { BlufStatement } from '../DomainIntelligence/components/BlufStatement/BlufStatement'
import { StatStrip } from '../DomainIntelligence/components/StatStrip/StatStrip'
import { SeverityTag } from '../DomainIntelligence/components/SeverityTag/SeverityTag'
import { SeverityDistributionBar } from '../DomainIntelligence/components/SeverityDistributionBar/SeverityDistributionBar'
import { ReadinessPanel } from '../DomainIntelligence/components/ReadinessPanel/ReadinessPanel'
import { ReportBreadcrumb } from '../DomainIntelligence/components/ReportBreadcrumb/ReportBreadcrumb'
import { ReportState } from '../DomainIntelligence/components/ReportState/ReportState'
import { InvestigationReport } from '../DomainIntelligence/components/InvestigationReport/InvestigationReport'
import { syntheticSpecimenReport } from './syntheticSpecimen'
import styles from './DIDesignGallery.module.scss'

/**
 * DIDesignGallery — every treatment and variation of the DI target-state
 * design, mounted live against the REAL components (and real HO1 fixture
 * data), so the page can never drift from the implementation. Admin-gated,
 * unlinked: /di-design-gallery. Flip the app theme switcher to verify both
 * themes — every treatment here is token-driven.
 *
 * This page is also the reference catalog for the upcoming Portal exercise:
 * the portal exercise should reuse the same grammar (severity encodings,
 * chip doctrine, type roles, states).
 */

const Section = ({ title, note, children }) => (
    <section className={styles.section}>
        <div className={styles.sectionHeader}>
            <ScoopText className={styles.sectionTitle}>{title}</ScoopText>
            {note && <ScoopText className={styles.sectionNote}>{note}</ScoopText>}
        </div>
        {children}
    </section>
)

const Caption = ({ children }) => <ScoopText className={styles.caption}>{children}</ScoopText>

// --- Severity mixes for the distribution-bar variations ---
const badRun = [
    ...Array(3).fill({ severity: 'severe' }),
    ...Array(5).fill({ severity: 'moderate' }),
    ...Array(6).fill({ severity: 'mild' }),
    ...Array(31).fill({ severity: 'healthy' }),
    ...Array(2).fill({ severity: 'healthy', unprobed: true }),
]
const cleanRun = [
    ...Array(1).fill({ severity: 'mild' }),
    ...Array(46).fill({ severity: 'healthy' }),
]

const distributionEntities = (list) => list.map((item, index) => ({ ...item, entityId: `e${index}` }))

// --- Doctrine before/after demo rows ---
const DoctrineBefore = () => (
    <div className={styles.demoRow}>
        <ScoopChip size="small" color="error" label="Severe" />
        <ScoopChip size="small" color="warning" label="Not probed" />
        <ScoopChip size="small" color="error" label="HIGH" />
        <ScoopChip size="small" color="warning" label="pending" />
        <ScoopChip size="small" label="District 7" />
        <ScoopChip size="small" color="primary" label="manual" />
    </div>
)

const DoctrineAfter = () => (
    <div className={styles.demoRow}>
        <SeverityTag severity="severe" />
        <span className={styles.warnText}>
            <WarningCircleIcon size={14} className={styles.warnIcon} /> Not probed
        </span>
        <ScoopText className={styles.priorityError}>HIGH</ScoopText>
        <ScoopChip size="small" color="warning" label="pending" />
        <ScoopText className={styles.metaText}>District 7 · manual</ScoopText>
    </div>
)

const miniTreeData = [
    {
        id: 'g1',
        label: 'Specimen Retail',
        nodeType: 'run_group',
        children: [
            {
                id: 'd1',
                label: 'Jul 27, 2026 — 5 flagged',
                nodeType: 'date',
                children: [
                    { id: 'e1', label: 'Store 1268 — Severe: labor cost +18%', nodeType: 'entity' },
                    { id: 'e2', label: 'Store 1012 — Moderate: inventory build', nodeType: 'entity' },
                ],
            },
        ],
    },
]

export const DIDesignGallery = () => {
    // SYNTHETIC specimen only (P0, 2026-07-29): real fixture dossiers were
    // statically imported here once, which bundled real customer data into the
    // public admin bundle. Never import from __fixtures__ in shipped code.
    const fixtureReport = syntheticSpecimenReport
    // Direction lab (structural design directions — the geometry token layer,
    // not color themes): current / ops-console / editorial.
    const [direction, setDirection] = useState('current')
    const directionClass =
        direction === 'console'
            ? styles.dirConsole
            : direction === 'editorial'
              ? styles.dirEditorial
              : ''

    return (
        <div className={styles.page}>
            <header className={styles.pageHeader}>
                <ScoopText className={styles.pageTitle}>DI Design Treatments</ScoopText>
                <ScoopText className={styles.pageSubtitle}>
                    Every treatment of the target-state design, live against the real components
                    and real fixture data. Flip the app theme switcher — everything here is
                    token-driven and must hold in both themes. This page is the reference
                    catalog for the Portal exercise.
                </ScoopText>
            </header>

            <Section
                title="1 · The severity grammar"
                note="Severity appears in exactly two forms — the tag (tables/headers) and the dot+word (dense contexts). Color never rides alone."
            >
                <div className={styles.demoRow}>
                    <SeverityTag severity="severe" />
                    <SeverityTag severity="moderate" />
                    <SeverityTag severity="mild" />
                    <SeverityTag severity="healthy" />
                    <SeverityTag severity="something-unmapped" />
                </div>
                <Caption>Canonical four plus the unknown-echo fallback. Aliases (watch/critical) normalize through utils/severity.js.</Caption>
                <SeverityDistributionBar entities={distributionEntities(badRun)} entitiesNoun="entities" />
                <Caption>A run with problems: 3 severe, 5 moderate, 6 mild, 31 healthy, 2 never probed.</Caption>
                <SeverityDistributionBar entities={distributionEntities(cleanRun)} entitiesNoun="entities" />
                <Caption>A clean run reads clean in one pixel row.</Caption>
            </Section>

            <Section
                title="2 · The answer zone"
                note="BLUF as a typographic statement (not an alert); flagged/severe counts colored only when > 0."
            >
                <BlufStatement>
                    Sales fell at 5 of 47 entities, concentrated in District 7; two show the
                    inventory death-spiral pattern.
                </BlufStatement>
                <StatStrip
                    items={[
                        { value: 47, label: 'Scanned' },
                        { value: 5, label: 'Flagged', tone: 'warning' },
                        { value: 2, label: 'Severe', tone: 'error' },
                        { value: 3, label: 'Moderate', tone: 'warning' },
                        { value: 4, label: 'Actions' },
                    ]}
                />
                <StatStrip
                    items={[
                        { value: 47, label: 'Scanned' },
                        { value: 0, label: 'Flagged' },
                        { value: 0, label: 'Severe' },
                        { value: 0, label: 'Moderate' },
                        { value: 1, label: 'Actions' },
                    ]}
                />
                <Caption>Zero-count severities stay neutral — color means &quot;needs attention&quot;, not decoration.</Caption>
            </Section>

            <Section
                title="3 · Publish readiness"
                note="The publish decision's checklist: counts reconciliation, peer lenses, probe coverage — plus the two publish actions."
            >
                <ReadinessPanel
                    readiness={{
                        derived: { scanned: 47, flagged: 5 },
                        authoritative: { scanned: 47, flagged: 5 },
                        countMismatch: false,
                        unprobedCount: 0,
                        lensPairs: [],
                    }}
                />
                <ReadinessPanel
                    readiness={{
                        derived: { scanned: 41, flagged: 3 },
                        authoritative: { scanned: 47, flagged: 5 },
                        countMismatch: true,
                        unprobedCount: 2,
                        lensPairs: [['regional', 'national']],
                    }}
                />
                <ReadinessPanel
                    readiness={{
                        derived: { scanned: 12, flagged: 1 },
                        authoritative: null,
                        countMismatch: false,
                        unprobedCount: 0,
                        lensPairs: [],
                    }}
                />
                <Caption>Clean / all warnings firing / legacy run with no engine statistics (honest neutral line, not a false check).</Caption>
            </Section>

            <Section
                title="4 · The chip doctrine"
                note="Chips say state; text says data. The single biggest de-noising change."
            >
                <div className={styles.comparison}>
                    <div className={styles.compareCard}>
                        <ScoopText className={styles.compareLabel}>Before — chip wall</ScoopText>
                        <DoctrineBefore />
                    </div>
                    <div className={styles.compareCard}>
                        <ScoopText className={styles.compareLabel}>After — doctrine</ScoopText>
                        <DoctrineAfter />
                    </div>
                </div>
                <Caption>
                    Severity keeps the sole chip; &quot;Not probed&quot; is icon+text; priority is
                    colored text; scope/provenance is metadata text; status stays a chip (it is a state).
                </Caption>
            </Section>

            <Section
                title="5 · States"
                note="One empty/error pattern (ReportState), skeletons shaped like the destination, real progress."
            >
                <div className={styles.stateGrid}>
                    <ReportState
                        title="No runs yet"
                        message="Completed investigation runs appear here."
                        action={<span className={styles.fakeButton}>New run</span>}
                    />
                    <ReportState
                        variant="error"
                        title="Error loading report"
                        message="Failed to fetch artifact: 404"
                        action={<span className={styles.fakeButton}>Retry</span>}
                    />
                    <div className={styles.skeletonSpecimen}>
                        <ScoopSkeleton variant="text" width="40%" height={28} />
                        <ScoopSkeleton variant="text" width="60%" height={16} />
                        <ScoopSkeleton variant="rounded" height={80} />
                        <ScoopSkeleton variant="rounded" height={120} />
                    </div>
                    <div className={styles.progressSpecimen}>
                        <ScoopText className={styles.metaText}>HO1 · RUNNING</ScoopText>
                        <ScoopLinearProgress value={49} color="primary" />
                        <ScoopText className={styles.metaText}>23/47 screenings</ScoopText>
                    </div>
                </div>
            </Section>

            <Section
                title="6 · Navigation tracks"
                note="The drill path always visible: breadcrumbs in every masthead + a persistent tree highlight."
            >
                <ScoopColumn className={styles.demoColumn}>
                    <ReportBreadcrumb
                        crumbs={[{ label: 'Investigations', onClick: () => {} }, { label: 'Specimen Retail' }]}
                    />
                    <ReportBreadcrumb
                        crumbs={[
                            { label: 'Investigations', onClick: () => {} },
                            { label: 'Specimen Retail', onClick: () => {} },
                            { label: 'Jul 27, 2026' },
                        ]}
                    />
                    <ReportBreadcrumb
                        crumbs={[
                            { label: 'Investigations', onClick: () => {} },
                            { label: 'Specimen Retail', onClick: () => {} },
                            { label: 'Jul 27, 2026', onClick: () => {} },
                            { label: 'District 7', onClick: () => {} },
                            { label: 'Store 1268' },
                        ]}
                    />
                </ScoopColumn>
                <div className={styles.treeSpecimen}>
                    <ScoopTree
                        data={miniTreeData}
                        expandedByDefault
                        selectedNodes={[miniTreeData[0].children[0].children[0]]}
                        onNodeClick={() => {}}
                    />
                </div>
                <Caption>The selected node keeps its highlight (that background was silently broken in prod until the rgba sweep).</Caption>
            </Section>

            <Section
                title="7 · Type roles & soft tints"
                note="Seven roles, exhaustive. Soft tints pair with state colors in both themes."
            >
                <div className={styles.typeSpecimen}>
                    <ScoopText className={styles.typePageTitle}>Page title — xl semibold</ScoopText>
                    <ScoopText className={styles.typeSectionTitle}>Section title — base semibold</ScoopText>
                    <ScoopText className={styles.typeOverline}>OVERLINE — XS CAPS 0.05EM</ScoopText>
                    <ScoopText className={styles.typeBody}>Body — sm primary. The quick brown fox jumps over the lazy dog.</ScoopText>
                    <ScoopText className={styles.typeMeta}>Metadata — xs secondary · counts, dates, ids</ScoopText>
                    <ScoopText className={styles.typeCaption}>Caption — xs tertiary · decoration only</ScoopText>
                </div>
                <div className={styles.swatchRow}>
                    {[
                        ['success', '$color-success-soft'],
                        ['warning', '$color-warning-soft'],
                        ['error', '$color-error-soft'],
                        ['info', '$color-info-soft'],
                    ].map(([name]) => (
                        <div key={name} className={styles.swatch}>
                            <div className={`${styles.swatchBox} ${styles[`swatch-${name}`]}`} />
                            <ScoopText className={styles.typeCaption}>{name}-soft</ScoopText>
                        </div>
                    ))}
                </div>
            </Section>

            <Section
                title="8 · Full specimen + direction lab"
                note="The actual InvestigationReport against synthetic specimen data — under three structural directions. Same components; only the geometry (type, rhythm, radii, measure) changes. Color is settled and untouched."
            >
                <ScoopTab
                    tabs={[
                        { key: 'current', label: 'Current (target state)' },
                        { key: 'console', label: 'Ops console' },
                        { key: 'editorial', label: 'Editorial document' },
                    ]}
                    value={direction}
                    onChange={(_, value) => setDirection(value)}
                    variant="pill"
                    size="sm"
                    fullWidth={false}
                />
                <div className={`${styles.specimen} ${directionClass}`} key={direction}>
                    <div className={styles.syntheticBanner}>Synthetic specimen data</div>
                    <InvestigationReport
                        report={fixtureReport}
                        loadingReport={false}
                        reportError={null}
                        selectedRunPath="gallery/ho1-investigation-group"
                        generateArtifactDocx={() => {}}
                        isGeneratingArtifactDocx={false}
                        actionCandidates={[]}
                        onAcceptAction={() => {}}
                        onRejectAction={() => {}}
                        breadcrumbs={[
                            { label: 'Investigations', onClick: () => {} },
                            { label: 'HO1 (fixture)' },
                        ]}
                    />
                </div>
            </Section>

            <footer className={styles.pageFooter}>
                <ScoopText className={styles.caption}>
                    <InfoIcon size={12} /> Anything on this page that looks wrong IS wrong — the
                    page renders the production components, so a bug here is a bug in the tab.
                </ScoopText>
            </footer>
        </div>
    )
}
